import { useState, useMemo, useCallback } from "react";
import styled from "styled-components";
import {
  Card,
  Input,
  SimpleTableWrapper,
  SimpleTableToolbar,
  SimpleTableTitle,
  SimpleTableContainer,
  SimpleTable,
  SimpleTableHead,
  SimpleTableRow,
  SimpleTableHeader,
  SimpleTableBody,
  IconButton,
  IconDelete,
  SimpleTableData,
  PrimaryButton,
  preventDefaultBehavior,
  SecondaryButton,
  BUTTON_SIZES,
  Loader,
  Content,
  CONTENT_TYPES,
} from "cdk-radial";
import axios from "axios";

import withNavigationToBundles from "./withNavigationToBundles";
import PricingBreadcrumbs from "./PricingBreadcrumbs";
import { PRICING_LIST_PLANS, VIEW_BUNDLES } from "./screen";

import config from "../config/app.conf.json";
import { BUNDLE_PLAN_TYPES } from "../Utils/constants";

const UUID_REGEX = /^[\da-f]{8}-([\da-f]{4}-){3}[\da-f]{12}$/i;

const PlanDescSection = styled.div`
  margin-top: 1.5rem;
`;
const AddOrgForm = styled.form`
  display: flex;
  gap: 1rem;
`;
const AddOrgButton = styled(PrimaryButton)`
  height: 3rem;
`;
const ZebraSimpleTableRow = styled(SimpleTableRow)`
  :nth-child(even) {
    background-color: ${({ theme }) =>
      theme.color.zebraStripe.background.value};
    border-top: 1px solid ${({ theme }) => theme.color.gray[200].value};
    :hover {
      background-color: ${({ theme }) => theme.color.zebraStripe.hover.value};
    }
  }
  &:last-child {
    border-bottom: none;
  }
`;
const Actions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;
const ErrorContent = styled(Content)`
  color: ${({ theme }) => theme.color.system.negative[500].value};
`;

const unitMap = {
  subscription: "location",
  advanced: "month",
};

const orgIsValidForSharing = (org) => org.status === "approved"; // org must be approved...

function PricingSharePlan(props) {
  const {
    auth,
    setScreen,
    setShowSuccessToast,
    ApiBundleManagementReducer,
  } = props;

  const {selectedBundle, selectedPricingPlan} = ApiBundleManagementReducer;
  const { accessToken } = auth;
  const originalShares = selectedPricingPlan?.bundlePricePlanShare ?? [];

  const [inputOrgId, setInputOrgId] = useState("");
  const [shares, setShares] = useState(originalShares);
  const [orgFetchLoading, setOrgFetchLoading] = useState(false);
  const [orgFetchError, setOrgFetchError] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const breadcrumbs = useMemo(() => {
    return [
      { label: "Bundle Management", onClick: () => setScreen(VIEW_BUNDLES) },
      { label: "Bundle Pricing", onClick: () => setScreen(PRICING_LIST_PLANS) },
      { label: "Share Plan" },
    ];
  }, [setScreen]);

  const shareDifference = useMemo(() => {
    const originalOrgs = originalShares.map((share) => share.orgId);
    const currentOrgs = shares.map((share) => share.orgId);

    const toAdd = shares.filter((share) => !originalOrgs.includes(share.orgId));
    const toRemove = originalShares.filter(
      (share) => !currentOrgs.includes(share.orgId)
    );

    return { toAdd, toRemove };
  }, [originalShares, shares]);
  const dirty =
    shareDifference.toAdd.length > 0 || shareDifference.toRemove.length > 0;

  const addOrgToShare = useCallback(
    (orgId) => {
      if (!UUID_REGEX.test(orgId)) {
        setOrgFetchError("Invalid Organization ID format");
        return;
      }
      if (shares.some((share) => share.orgId === orgId)) {
        setOrgFetchError("Plan is already shared with this organization.");
        return;
      }
      setOrgFetchError(null);
      setOrgFetchLoading(true);
      axios
        .get(`${config.api.organizations_uri}/${orgId}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
        .then((res) => {
          const org = res.data;
          if (!orgIsValidForSharing(org)) {
            setOrgFetchError("Organization must be an approved organization.");
            return;
          }
          setShares((shares) => [
            ...shares,
            {
              // shareId not included, since the share doesn't exist yet
              orgId: org.id,
              orgName: org.name,
            },
          ]);
          setInputOrgId("");
        })
        .catch((error) => {
          if (error.response?.status === 404) {
            setOrgFetchError("Organization not found.");
          } else {
            console.error("Unable to fetch organization:", error);
            setOrgFetchError(`An error occurred: ${error.message}`);
          }
        })
        .finally(() => setOrgFetchLoading(false));
    },
    [shares, accessToken]
  );

  const removeOrgFromShare = useCallback((orgId) => {
    setShares((shares) => shares.filter((share) => share.orgId !== orgId));
  }, []);

  const saveShares = useCallback(() => {
    setSaveLoading(true);
    setSaveError(null);
    const promises = [];
    // 1. invoke create endpoint for new shares
    if (shareDifference.toAdd.length > 0) {
      promises.push(
        axios.post(
          `${config.api.api_gateway_url}/v1/admin/bundles/${selectedBundle.id}/price-plans/${selectedPricingPlan?.id}/share`,
          { orgIds: shareDifference.toAdd.map((share) => share.orgId) },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        )
      );
    }
    // 2. invoke delete endpoint for removed shares
    promises.push(
      ...shareDifference.toRemove.map((share) =>
        axios.delete(
          `${config.api.api_gateway_url}/v1/admin/bundles/${selectedBundle.id}/price-plan/share/${share.shareId}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        )
      )
    );

    return Promise.all(promises)
      .then(() => {
        setShowSuccessToast(true);
        setScreen(PRICING_LIST_PLANS);
      })
      .catch((error) => {
        setSaveError(`An error occurred: ${error.message}`);
      })
      .finally(() => setSaveLoading(false));
  }, [
    shareDifference,
    setShowSuccessToast,
    setScreen,
    accessToken,
    selectedBundle,
    selectedPricingPlan,
  ]);

  const getPlanTypeDescription = (plan) => {
  if (!plan?.planType || plan.price == null) return "";
  
  const unit = unitMap[plan.planType];
  if (!unit) return "";
  
  const planLabel = BUNDLE_PLAN_TYPES[plan.planType] || "Unknown Plan Type";
  return `Plan: ${planLabel} Pricing, $${plan.price} / ${unit}`;
};
  return saveLoading ? (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "300px",
      }}
    >
      <Loader
        className="pricing-plans-loader"
        data-testid="pricing_plans_loader"
        label="Saving..."
        variant="inline"
      />
    </div>
  ) : (
    <Card>
      <div className="create-bundle-card">
        <div className="bundle-pricing-title">
          Pricing - {selectedBundle?.name}
        </div>

        <PricingBreadcrumbs breadcrumbs={breadcrumbs} />

        <div>
          <div className="bundle-pricing-text">Share Plan</div>
          <div />
          <div className="bundle_pricing_description">
            Bundle pricing is a flat monthly fee per location for unlimited API
            usage. Public plans are visible to all, while private plans are
            shared with specific organizations. ISV selections appear in the API
            Directory, and dealer selections are shown in the Dealer Portal for
            API buyers.
          </div>
        </div>

        <PlanDescSection>
          <div className="bundle-pricing-text">
            {selectedPricingPlan?.planName}
          </div>
          <div />
          <div className="bundle_pricing_description">
            {getPlanTypeDescription(selectedPricingPlan)}
          </div>
          <div className="bundle_pricing_description">
            Catalog: {selectedPricingPlan?.catalogNumber}
          </div>
        </PlanDescSection>

        <AddOrgForm
          onSubmit={(e) => {
            preventDefaultBehavior(e);
            addOrgToShare(inputOrgId);
          }}
        >
          <Input
            id="bundle-pricing-share-with-org"
            name="bundle-pricing-share-with-org"
            label="Share with Organization"
            placeholder="Organization ID"
            value={inputOrgId}
            onChange={(e) => setInputOrgId(e.target.value)}
            isDisabled={orgFetchLoading}
            enableCustomValidation
            hasError={!!orgFetchError}
            errorMessage={orgFetchError}
          />
          <AddOrgButton
            text="Add"
            size={BUTTON_SIZES.LARGE}
            isLoading={orgFetchLoading}
            isDisabled={orgFetchLoading}
            onClick={() => addOrgToShare(inputOrgId)}
          />
        </AddOrgForm>

        <SimpleTableWrapper>
          <SimpleTableToolbar>
            <SimpleTableTitle>Organizations ({shares.length})</SimpleTableTitle>
          </SimpleTableToolbar>
          <SimpleTableContainer>
            <SimpleTable>
              <SimpleTableHead>
                <SimpleTableRow>
                  <SimpleTableHeader>Name</SimpleTableHeader>
                  <SimpleTableHeader>ID</SimpleTableHeader>
                  <SimpleTableHeader textAlign="right">
                    Remove
                  </SimpleTableHeader>
                </SimpleTableRow>
              </SimpleTableHead>
              <SimpleTableBody>
                {shares.map((org) => (
                  <ZebraSimpleTableRow key={org.orgId}>
                    <SimpleTableData>{org.orgName}</SimpleTableData>
                    <SimpleTableData>{org.orgId}</SimpleTableData>
                    <SimpleTableData textAlign="right">
                      <IconButton
                        icon={<IconDelete />}
                        text="Remove"
                        onClick={() => removeOrgFromShare(org.orgId)}
                      />
                    </SimpleTableData>
                  </ZebraSimpleTableRow>
                ))}
              </SimpleTableBody>
            </SimpleTable>
          </SimpleTableContainer>
        </SimpleTableWrapper>

        {saveError && (
          <ErrorContent type={CONTENT_TYPES.BODY_2}>{saveError}</ErrorContent>
        )}

        <Actions>
          <PrimaryButton
            text="Save"
            onClick={saveShares}
            isDisabled={!dirty || saveLoading || orgFetchLoading}
          />
          <SecondaryButton
            text="Cancel"
            onClick={() => setScreen(PRICING_LIST_PLANS)}
          />
        </Actions>
      </div>
    </Card>
  );
}

export default withNavigationToBundles(PricingSharePlan);
